import { userAuthContextType } from '../../auth/AuthProvider';
import { axios, getResponse, settings } from '../../helpers/main';

const { backend } = settings;
const authServices = { user: userAuthContextType }; //eslint-disable-line
const objectService = getResponse({ type: 0 }); //eslint-disable-line

/**
 * Service to manage communication with the SystemAccesses API.
 * Follows the Single Responsibility Principle (SRP) by separating network logic from the UI.
 */
export const SystemAccessService = {
    /**
     * Retrieves all SystemAccesses and related master data.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    getAll: async ({ auth, languageId, subMethod = '' } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.systemAccesses}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                method: backend?.methods?.systemAccesses?.getAll,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Retrieves data needed to create a new SystemAccess.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    getAdd: async ({ auth, languageId, subMethod = '' } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.systemAccesses}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                method: backend?.methods?.systemAccesses?.getAdd,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Retrieves a SystemAccess by ID, optionally with details.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number} params.id - SystemAccess ID.
     * @param {boolean} [params.withDetails=false] - Whether to include details.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    getById: async ({ auth, id, withDetails = false, languageId, subMethod = '' } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.systemAccesses}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                id: id,
                withDetails: withDetails,
                method: backend?.methods?.systemAccesses?.getById,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Creates a new SystemAccess.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {Object} params.systemAccess - SystemAccess data to create.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    create: async ({ auth, systemAccess, languageId, subMethod = '' } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.systemAccesses}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                data: systemAccess,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Updates an existing SystemAccess.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {Object} params.systemAccess - SystemAccess data to update.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    update: async ({ auth, systemAccess, languageId, subMethod = '' } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.systemAccesses}/${systemAccess?.id || 0}`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                data: systemAccess,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Generates a PDF report for a SystemAccess.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number} params.id - SystemAccess ID.
     * @param {string} params.languageCode - Current language code.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    getReport: async ({ auth, id, languageCode, subMethod = '' } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.systemAccesses}/${backend?.methods?.main?.reports?.pdf}`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                id: id,
                languageCode: languageCode,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
    * Deletes a SystemAccess by ID.
    * @param {Object} params - Parameters.
    * @param {authServices} params.auth - User authentication object.
    * @param {number} params.id - SystemAccess ID to delete.
    * @param {number} params.languageId - Current language ID.
    * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
    * @returns {Promise<objectService>} Promise with the API response.
    */
    delete: async ({ auth, id, languageId, subMethod = '' } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.systemAccesses}/${id}`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.delete(requestObj);
    },

    /**
     * Retrieves a SystemAccessDetail by ID.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number} params.id - SystemAccessDetail ID.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    getDetailById: async ({ auth, id, languageId, subMethod = '' } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.systemAccessDetails}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                id: id,
                languageId: languageId,
                method: backend?.methods?.systemAccessDetails?.getById,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Deletes a SystemAccessDetail by ID.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number} params.id - SystemAccessDetail ID to delete.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    deleteDetail: async ({ auth, id, languageId, subMethod = '' } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.systemAccessDetails}/${id}`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.delete(requestObj);
    },

    /**
     * Creates a new SystemAccessDetail.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {Object} params.systemAccessDetail - SystemAccessDetail data to create.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    createDetail: async ({ auth, systemAccessDetail, languageId, subMethod = '' } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.systemAccessDetails}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                data: systemAccessDetail,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Updates an existing SystemAccessDetail.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {Object} params.systemAccessDetail - SystemAccessDetail data to update.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    updateDetail: async ({ auth, systemAccessDetail, languageId, subMethod = '' } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.systemAccessDetails}/${systemAccessDetail?.id || 0}`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                data: systemAccessDetail,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },
};